// Helper pembaruan versi aplikasi (deploy baru) — dipakai useAppUpdate, LoginModal,
// chunkReloadFallback, dan ChunckErrorFallback supaya aturan "aman reload" cuma satu sumber.

// Kata kunci path halaman input. Di halaman ini reload otomatis dilarang karena bisa
// menghapus data yang belum disimpan user.
const UNSAFE_PATH_KEYWORDS = [
  'add',
  'edit',
  'import',
  'transfer',
  'spend',
  'receive',
  'register',
  'dispose',
  'cashier',
  'web-pos',
  'multi-invoice-payment',
  'multi-purchase-payment',
  'conversionbalances',
  'conversions',
  'price-rule',
]

// Event yang dikirim saat chunk gagal dimuat di halaman input, supaya notifikasi
// versi baru langsung tampil tanpa reload paksa.
export const APP_UPDATE_EVENT = 'kledo:update-available'

export const isSafeToReloadPath = (pathname = '') =>
  !UNSAFE_PATH_KEYWORDS.some((keyword) => pathname.includes(keyword))

// App memakai hash router: '#/sales/invoices/add?x=1' -> '/sales/invoices/add'
export const getHashPathname = (hash = window.location.hash) =>
  hash.replace(/^#/, '').split('?')[0] || '/'

// Penanda jumlah percobaan reload karena chunk error, per tab (sessionStorage).
// Diberi versi build supaya tiap deploy dapat jatah percobaan baru — kalau tidak,
// tab yang terbuka melewati beberapa deploy berhenti reload otomatis dan user
// melihat layar error.
export const getChunkReloadKey = () =>
  `didReloadForChunkMismatch:${import.meta.env.PACKAGE_VERSION}`

// Key localStorage versi aktif milik react-clear-browser-cache (default storageKey-nya).
// Diisi sebelum reload supaya pengecekan saat boot tidak memicu reload kedua.
const APP_VERSION_STORAGE_KEY = 'APP_VERSION'

// meta.json adalah file statis same-origin, bukan endpoint API — sengaja tidak lewat
// instance axios supaya tidak kena baseURL & interceptor auth. Sama seperti cara
// react-clear-browser-cache mengecek versi.
export const fetchLatestAppVersion = async () => {
  const response = await fetch(`/meta.json?time=${Date.now()}`, { cache: 'no-store' })
  const meta = await response.json()
  return meta?.version
}

// Batas jumlah chunk yang dicek per error, supaya penelusuran import tidak jalan tanpa ujung.
const MAX_CHUNKS_TO_CHECK = 300
// Batas waktu penelusuran. Lewat dari ini, reload tetap jalan.
const REFRESH_CHUNKS_TIMEOUT = 5000

// Ambil URL modul dari pesan chunk error. Safari tidak menulis URL, jadi hasilnya null.
// Chrome: 'Failed to fetch dynamically imported module: https://app.kledo.com/assets/x.js'
// Firefox: 'error loading dynamically imported module: https://app.kledo.com/assets/x.js'
export const getFailedChunkUrl = (error) => {
  const message = String(error?.message || error?.reason || '')
  const match = message.match(/https?:\/\/[^\s'"]+?\.js(?!\w)/)
  if (!match) {
    return null
  }

  try {
    const url = new URL(match[0])
    return url.origin === window.location.origin ? url.href : null
  } catch {
    return null
  }
}

// Ambil import statis dari hasil build Vite, mis. `import{a as b}from"./x.js"` atau `import"./x.js"`.
// Dynamic import `import("./x.js")` tidak ikut, karena modul itu dimuat terpisah nanti.
export const getStaticImportUrls = (code, baseUrl) =>
  [...code.matchAll(/(?:\bfrom|\bimport)\s*["']([^"']+?\.js)["']/g)]
    .map(([, specifier]) => new URL(specifier, baseUrl))
    .filter((url) => url.origin === new URL(baseUrl).origin)
    .map((url) => url.href)

const isJavaScriptResponse = (response) =>
  response.ok && (response.headers.get('content-type') || '').includes('javascript')

// Cek satu chunk. Kalau cache browser berisi respons yang bukan JS (mis. index.html dari
// fallback SPA Cloudflare dengan header immutable), minta ulang tanpa cache supaya
// isi cache tertimpa. Hasilnya daftar import statis chunk itu.
const checkChunk = async (url, onRefresh) => {
  try {
    let response = await fetch(url)
    if (!isJavaScriptResponse(response)) {
      response = await fetch(url, { cache: 'reload' })
      onRefresh()
    }

    if (!isJavaScriptResponse(response)) {
      return []
    }

    return getStaticImportUrls(await response.text(), url)
  } catch {
    // offline / fetch gagal — chunk ini dilewati
    return []
  }
}

// Cek chunk per level import secara paralel. Level berikutnya butuh hasil level ini.
const crawlChunks = async (level, visited, onRefresh) => {
  if (!level.length || visited.size >= MAX_CHUNKS_TO_CHECK) {
    return
  }

  level.forEach((url) => visited.add(url))
  const imports = await Promise.all(level.map((url) => checkChunk(url, onRefresh)))
  const nextLevel = [...new Set(imports.flat())].filter((url) => !visited.has(url))
  await crawlChunks(nextLevel, visited, onRefresh)
}

// Pesan error hanya menyebut chunk yang di-import, padahal yang rusak bisa dependency-nya.
// Jadi telusuri semua import statis dari chunk itu. Reload saja tidak cukup: browser memakai
// cache immutable dan tidak meminta chunk ke server lagi. Hasilnya jumlah chunk yang diminta ulang.
export const refreshBrokenChunks = (error) => {
  const startUrl = getFailedChunkUrl(error)
  if (!startUrl) {
    return Promise.resolve(0)
  }

  const timeout = new Promise((resolve) => {
    setTimeout(() => {
      resolve(0)
    }, REFRESH_CHUNKS_TIMEOUT)
  })

  let refreshed = 0
  const crawl = crawlChunks([startUrl], new Set(), () => {
    refreshed += 1
  }).then(() => refreshed)

  return Promise.race([crawl, timeout])
}

// Langkah hapus cache sesuai perangkat, untuk modal panduan di layar "Coba Lagi".
// Hasilnya daftar nama langkah; teksnya ada di locales (notification.cache_guide_step_<nama>).
export const getCacheGuideSteps = ({
  userAgent = navigator.userAgent,
  platform = navigator.platform,
  maxTouchPoints = navigator.maxTouchPoints,
  standalone = window.matchMedia?.('(display-mode: standalone)').matches,
} = {}) => {
  const isMac = /Mac/i.test(platform)
  // iPad versi baru mengaku Mac, jadi Mac dengan layar sentuh dianggap tablet
  if (/Android|iPhone|iPad|iPod/i.test(userAgent) || (isMac && maxTouchPoints > 1)) {
    return ['mobile_settings', 'mobile_cache']
  }

  const isSafari = /Safari/i.test(userAgent) && !/Chrome|Chromium|Edg|Firefox|OPR/i.test(userAgent)
  if (isMac && isSafari) {
    return ['safari_develop', 'safari_empty']
  }

  return [
    // App desktop (PWA) tidak punya menu hapus data, jadi buka dulu di tab browser
    ...(standalone ? ['open_in_browser'] : []),
    isMac ? 'shortcut_mac' : 'shortcut_windows',
    'range',
    'cache_only',
    'delete',
  ]
}

export const reloadToVersion = (version) => {
  if (version) {
    try {
      window.localStorage.setItem(APP_VERSION_STORAGE_KEY, version)
    } catch {
      // localStorage tidak tersedia (mode private) — boot berikutnya cukup reload sekali lagi
    }
  }
  window.location.reload()
}
